import React, { useEffect } from 'react';
import { CONCIERGE_REQUEST_CONTEXT_EVENT, ConciergeContextRequestEvent } from 'app/concierge/conciergeContext';
import { REPORT_CONTEXT_CAPTURE } from 'app/concierge/reportContextCapture';
import { useIsConfigConstantEqual } from 'hooks/useConfigConstant';

export interface IConciergeContext {
    providers: Map<string, () => Promise<any>>;
    register: (key: string, func: () => Promise<any>) => void;
    unregister: (key: string) => void;
    buildContext: () => Promise<any>;
}

const conciergeContextValue: IConciergeContext = {
    providers: new Map<string, () => Promise<any>>(),
    register: function (key: string, func: () => Promise<any>) {
        this.providers.set(key, func);
    },
    unregister: function (key: string) {
        this.providers.delete(key);
    },
    buildContext: async function () {
        const context: any = {};
        for (const [key, providerFunc] of this.providers.entries()) {
            context[key] = await providerFunc();
        }
        context.timestamp = new Date().toISOString();
        return context;
    },
};

export const ConciergeContext = React.createContext<IConciergeContext>(conciergeContextValue);

export function useSetupConciergeContext() {
    const handleContextRequest = async (event: Event) => {
        const context = await conciergeContextValue.buildContext();
        if (context) {
            (event as ConciergeContextRequestEvent).detail.respond(context);
        }
    };

    useEffect(() => {
        window.addEventListener(CONCIERGE_REQUEST_CONTEXT_EVENT, handleContextRequest);
        return () => {
            window.removeEventListener(CONCIERGE_REQUEST_CONTEXT_EVENT, handleContextRequest);
        };
    }, [handleContextRequest]);

    return conciergeContextValue;
}

// Row 2675: whether report context is captured for Concierge (off unless the page constant is true)
export function useReportContextCapture(): boolean {
    return useIsConfigConstantEqual(REPORT_CONTEXT_CAPTURE, true);
}

export function useConciergeContext(key: string, func: () => Promise<any>, enabled = true): any {
    const context = React.useContext(ConciergeContext);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        context.register(key, func);
        return () => {
            context.unregister(key);
        };
    }, [context, key, func, enabled]);
}
